import { useContext } from 'react'
import { SiteConfigContext } from '../SiteConfigProvider'

const removeSpecialCharacters = (content) => {
	return content
		.replace(/(<([^>]+)>)/gi, '')
		.replace(/"/g, "'")
		.replace(/&([a-z0-9]+|#[0-9]{1,6}|#x[0-9a-f]{1,6});/g, '')
}

/**
 * Function to generate Article ld+json schema
 *
 * @param {Object} props
 * @param {Object} props.author
 * @param {String} props.author.name
 * @param {String} props.canonical
 * @param {String} props.content
 * @param {String} props.published
 * @param {String} props.lastModified
 * @param {String} props.title
 * @param {Object} props.thumbnail
 *
 * @returns {import('schema-dts').Article}
 */
export function ArticleSchema({ author, canonical, content, lastModified, published, title, thumbnail }) {
	// ensure there are no html tag elements within the articleBody and author description
	// @todo: move this to utils, think this will be used in other schemas or areas
	const finalTextContent = removeSpecialCharacters(content)
	const textAuthor = author.bio ? author.bio.replace(/(<([^>]+)>)/gi, '') : ''
	const {
		siteConstants: {
			ARTICLE_SCHEMA_PROPS: { name, logoHeight, logoWidth, logoUrl },
		},
	} = useContext(SiteConfigContext)

	return {
		'@context': 'http://schema.org',
		'@type': 'Article',
		articleBody: finalTextContent,
		author: {
			'@type': 'Person',
			name: author.name,
			description: textAuthor,
			image: author.thumbnail,
		},
		datePublished: published,
		dateModified: lastModified,
		inLanguage: 'en-us',
		headline: title,
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': canonical,
		},
		publisher: {
			'@type': 'Organization',
			name: name,
			logo: {
				'@type': 'ImageObject',
				url: logoUrl,
				width: logoWidth,
				height: logoHeight,
			},
		},
		image: {
			'@type': 'ImageObject',
			url: thumbnail.path,
		},
	}
}

/**
 * Function to generate Breadcrumb schema ld+json schema
 *
 * @param {Object} props
 * @param {Object} props.primary
 * @param {String} props.primary.name
 * @param {String} props.primary.slug
 * @param {Object} props.sub
 * @param {String} props.sub.name
 * @param {String} props.sub.slug
 *
 * @returns {import('schema-dts').BreadcrumbList}
 */
export function BreadcrumbSchema({ primary = null, sub = null }) {
	const {
		siteConstants: { SITE_HOST },
	} = useContext(SiteConfigContext)

	const primaryData = primary && {
		'@type': 'ListItem',
		position: 2,
		item: {
			'@id': `${SITE_HOST}/${primary.slug}`,
			name: primary.name,
		},
	}
	const subData = sub && {
		'@type': 'ListItem',
		position: 3,
		item: {
			// todo: update to use SITE_HOST from context and wrap story with MockSiteConfigProvider
			'@id': `${SITE_HOST}/${sub.slug}`,
			name: sub.name,
		},
	}
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				item: {
					'@id': `${SITE_HOST}`,
					name: 'Home',
				},
			},
			primary && primary.slug ? primaryData : '',
			sub && sub.slug ? subData : '',
		],
	}
}

/**
 *
 * @param {String} props.canonical :: current url/canonical of the page
 * @param {Array} props.items :: list of different services or items
 * @param {String} props.title :: name of the page
 */
export function ItemListSchema({
	authorName,
	canonical,
	items,
	items: { isFeaturedBestPick = false },
	modifiedDate,
	publishedDate,
	title,
}) {
	return {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		itemListOrder: 'https://schema.org/ItemListOrderDescending',
		name: title,
		numberOfItems: items.length,
		url: canonical,
		itemListElement: items.map((item, i) => {
			const {
				bestPickDescription,
				fullTitle,
				review: { score, reviewContent },
				serviceName,
				thumbnail,
			} = item

			let reviewRating
			if (score > 0) {
				reviewRating = {
					'@type': 'Rating',
					ratingValue: score,
					bestRating: 10,
					worstRating: 0,
				}
			}

			return (
				!isFeaturedBestPick && {
					'@type': 'ListItem',
					position: i + 1,
					name: fullTitle,
					item: {
						'@type': 'Product',
						name: serviceName,
						url: `${canonical}#${serviceName.replace(/\s/g, '-')}`,
						description: bestPickDescription,
						image: {
							'@type': 'ImageObject',
							url: thumbnail,
						},
						review: {
							'@type': 'Review',
							datePublished: publishedDate,
							dateModified: modifiedDate,
							reviewBody: removeSpecialCharacters(reviewContent || ''),
							reviewRating,
							author: {
								'@type': 'Person',
								name: authorName,
							},
						},
						brand: {
							'@type': 'Thing',
							name: serviceName,
						},
					},
				}
			)
		}),
	}
}

/**
 * Function to generate Organization Schema
 *
 * @param {String} canonical website canonical url
 * @param {String} logoUrl ogImage url defined in the meta data
 * @param {Array} sameAsUrls array of sameAs website urls
 *
 * @returns {import('schema-dts').Organization}
 */
export function OrganizationSchema(canonical, logoUrl, sameAsUrls) {
	return {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		url: canonical,
		logo: logoUrl,
		sameAs: sameAsUrls && sameAsUrls.map((url) => url),
	}
}

/**
 * Function to generate Person ld+json schema
 *
 * @param {Object} props
 * @param {Array} props.educations
 * @param {Object} props.users
 *
 * @returns {import('schema-dts').Person}
 */
export function PersonSchema({
	educations,
	users: {
		about_me,
		business,
		business: { job_title, website },
		contact: { zip, city, phone, street, state, email },
		image,
		name,
		social,
	},
}) {
	return {
		'@context': 'https://schema.org',
		'@type': 'Person',
		image: {
			'@type': 'ImageObject',
			url: `https://www.business.com${image}`,
			caption: name,
		},
		address: {
			'@type': 'PostalAddress',
			addressLocality: city,
			addressRegion: state,
			postalCode: zip,
			streetAddress: street,
		},
		description: about_me,
		email: email,
		jobTitle: job_title,
		name: name,
		alumniOf:
			educations &&
			educations.map((education) => {
				const { institution } = education
				return {
					name: institution,
				}
			}),
		telephone: phone,
		url: 'http://www.example.com',
		sameAs:
			social &&
			Object.values(social)
				.filter((socialLink) => socialLink !== '')
				.map((socialLink) => socialLink),
		worksFor: {
			'@type': 'Organization',
			'@id': 'website.com/#organization',
			name: business.name,
			url: website,
		},
	}
}

/**
 * Function to generate WebSite Schema
 *
 * @param {String} canonical WebSite canonical url
 *
 * @returns {import('schema-dts').WebSite}
 */
export function SearchSchema(canonical) {
	return {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		url: `${canonical}`,
		potentialAction: {
			'@type': 'SearchAction',
			target: `${canonical}/search/?q={search_term_string}`,
			'query-input': 'required name=search_term_string',
		},
	}
}
